import { Text } from '@thanx/uikit/text'
import type { FrequencyPeriod } from 'api/recoveryAi.types'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { FormControl, InputGroup } from 'react-bootstrap'
import { useStyletron } from 'styletron-react'
import InlinePeriodSelect from './InlinePeriodSelect'

const t = buildTranslate('recovery_ai.wizard.limits')

const THRESHOLD_INPUT_WIDTH = '70px' // compact number-input width (see thresholdInputStyle)
const RULE_GAP = '8px' // existing inline-sentence horizontal gap
// Display-format fallback for the gaming period <select> when the consumer opts
// into the section but supplies no period yet (the pane hydrates to this too).
const DEFAULT_DISPLAY_PERIOD: FrequencyPeriod = '30d'

// Partial the controlled parent can persist — each control emits only the
// field it owns so the consumer (wizard step or Settings pane) merges it into
// its own state without the body knowing how that state is stored.
export type EscalationRulesChange = {
  frequentThreshold?: string
  frequencyPeriod?: FrequencyPeriod
  repeatedThreshold?: string
  // AI-302 (FR-41): suspected-gaming threshold + period, in the display format.
  autoResolutionThreshold?: string
  autoResolutionPeriod?: FrequencyPeriod
}

type Props = {
  frequentThreshold: string
  frequencyPeriod: FrequencyPeriod
  repeatedThreshold: string
  // AI-302 (FR-41): OPTIONAL. When `autoResolutionThreshold` is provided (the
  // Settings pane), the suspected-gaming section renders; the wizard limits step
  // omits them, so it stays visually unchanged (no new required prop).
  autoResolutionThreshold?: string
  autoResolutionPeriod?: FrequencyPeriod
  onChange: (partial: EscalationRulesChange) => void
  // AI-64: when true, every control renders read-only (native disabled).
  // Optional so the wizard limits step stays editable unchanged; the Settings
  // pane passes `!canManage` so view-only operators get a read-only view.
  disabled?: boolean
}

// Chrome-free field markup for the escalation rules (AI-903): holds no state
// and never fetches. Extracted from EscalationRulesCard so the Settings pane
// can render it directly under SettingsPaneChrome instead of wrapping it in a
// second ConfigCard box (the wizard limits step and the Agent-Tuning modal
// still get the ConfigCard chrome via EscalationRulesCard, unchanged).
const EscalationRulesBody = ({
  frequentThreshold,
  frequencyPeriod,
  repeatedThreshold,
  autoResolutionThreshold,
  autoResolutionPeriod,
  onChange,
  disabled,
}: Props) => {
  const [css] = useStyletron()

  // Opt-in: only the Settings pane passes the gaming threshold, so the wizard
  // limits step never shows this third section.
  const showAutoResolution = autoResolutionThreshold !== undefined

  const addonStyle = css({
    backgroundColor: palette.grey20,
    color: palette.grey90,
    fontWeight: 600,
    fontSize: '14px',
  })

  // bootstrap-sass v3's `.input-group .form-control { width: 100% }` (2-class
  // specificity) beats a single-class styletron width, so a plain width here is
  // a no-op and the input balloons to its intrinsic size, leaving a wide gap
  // before the gray addon. !important wins regardless of source order (the rule
  // it overrides has none) so the addon hugs the number.
  const thresholdInputStyle = css({
    width: `${THRESHOLD_INPUT_WIDTH} !important`,
  })

  return (
    <>
      {/* Frequent complaints */}
      <div className="d-flex flex-column">
        <Text.BodyText4 bold>{t('frequent_complaints')}</Text.BodyText4>
        <div
          className={`d-flex align-items-center flex-wrap mt-xs ${css({
            gap: RULE_GAP,
          })}`}
        >
          <Text.BodyText4 color="grey70">
            {t('frequent_inline_prefix')}
          </Text.BodyText4>
          <InputGroup className={css({ width: 'auto' })}>
            <FormControl
              disabled={disabled}
              type="number"
              min={1}
              value={frequentThreshold}
              onChange={(e: React.FormEvent<FormControl>) =>
                onChange({
                  frequentThreshold: (e.target as HTMLInputElement).value,
                })
              }
              aria-label={t('frequent_complaints')}
              className={thresholdInputStyle}
            />
            <InputGroup.Addon className={addonStyle}>
              {t('complaints_suffix')}
            </InputGroup.Addon>
          </InputGroup>
          <Text.BodyText4 color="grey70">
            {t('frequent_inline_suffix')}
          </Text.BodyText4>
          <InlinePeriodSelect
            disabled={disabled}
            value={frequencyPeriod}
            onChange={period => onChange({ frequencyPeriod: period })}
            ariaLabel={t('frequent_period_aria')}
          />
        </div>
        <Text.BodyText5 color="grey50" className="mt-xs">
          {t('frequent_helper')}
        </Text.BodyText5>
      </div>

      {/* Consecutive complaints. This body renders its sections as bare
          Fragment children — it owns NO inter-section margin itself. The
          24px gap between Frequent / Consecutive / Suspected-abuse comes
          entirely from whichever flex-column wrapper the CONSUMER provides:
          ConfigCard's own `gap: CONFIG_CARD_GAP` for the wizard step /
          Agent-Tuning modal, or EscalationRulesPane's matching wrapper (same
          constant) for the Settings pane. Do NOT add a margin here — that
          would double up the gap on the ConfigCard path. */}
      <div className="d-flex flex-column">
        <Text.BodyText4 bold>{t('consecutive_complaints')}</Text.BodyText4>
        <div
          className={`d-flex align-items-center flex-wrap mt-xs ${css({
            gap: RULE_GAP,
          })}`}
        >
          <Text.BodyText4 color="grey70">
            {t('consecutive_inline_prefix')}
          </Text.BodyText4>
          <InputGroup className={css({ width: 'auto' })}>
            <FormControl
              disabled={disabled}
              type="number"
              min={1}
              value={repeatedThreshold}
              onChange={(e: React.FormEvent<FormControl>) =>
                onChange({
                  repeatedThreshold: (e.target as HTMLInputElement).value,
                })
              }
              aria-label={t('consecutive_complaints')}
              className={thresholdInputStyle}
            />
            <InputGroup.Addon className={addonStyle}>
              {t('consecutive_complaints_suffix')}
            </InputGroup.Addon>
          </InputGroup>
          <Text.BodyText4 color="grey70">
            {t('consecutive_inline_suffix')}
          </Text.BodyText4>
        </div>
        <Text.BodyText5 color="grey50" className="mt-xs">
          {t('consecutive_helper')}
        </Text.BodyText5>
      </div>

      {/* Suspected abuse / auto-resolution limit (AI-302, FR-41). Mirrors the
          FREQUENT block (threshold + period). Opt-in via props — the wizard
          limits step never renders it. Flags for review; does NOT block. */}
      {showAutoResolution && (
        <div className="d-flex flex-column">
          <Text.BodyText4 bold>{t('auto_resolution_gaming')}</Text.BodyText4>
          <div
            className={`d-flex align-items-center flex-wrap mt-xs ${css({
              gap: RULE_GAP,
            })}`}
          >
            <Text.BodyText4 color="grey70">
              {t('auto_resolution_inline_prefix')}
            </Text.BodyText4>
            <InputGroup className={css({ width: 'auto' })}>
              <FormControl
                disabled={disabled}
                type="number"
                min={1}
                value={autoResolutionThreshold}
                onChange={(e: React.FormEvent<FormControl>) =>
                  onChange({
                    autoResolutionThreshold: (e.target as HTMLInputElement)
                      .value,
                  })
                }
                aria-label={t('auto_resolution_gaming')}
                className={thresholdInputStyle}
              />
              <InputGroup.Addon className={addonStyle}>
                {t('auto_resolution_suffix')}
              </InputGroup.Addon>
            </InputGroup>
            <Text.BodyText4 color="grey70">
              {t('auto_resolution_inline_suffix')}
            </Text.BodyText4>
            <InlinePeriodSelect
              disabled={disabled}
              value={autoResolutionPeriod ?? DEFAULT_DISPLAY_PERIOD}
              onChange={period => onChange({ autoResolutionPeriod: period })}
              ariaLabel={t('auto_resolution_period_aria')}
            />
          </div>
          <Text.BodyText5 color="grey50" className="mt-xs">
            {t('auto_resolution_helper')}
          </Text.BodyText5>
        </div>
      )}
    </>
  )
}

export default EscalationRulesBody
